import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/contextAuth";
import {
  getActiveUrlScanJob,
  getUrlResults,
  getUrlScanJob
} from "../../api/get/get-web";
import { scanUrl } from "../../api/post/post-web";

const TERMINAL_JOB_STATUSES = new Set([
  "completed",
  "completed_with_warnings",
  "failed"
]);
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_TIME_MS = 45 * 60 * 1000;

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const useWebData = () => {
  const { userId, user } = useAuth();

  const [webLoading, setWebLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState(null);
  const [authorizationConfirmed, setAuthorizationConfirmed] = useState(false);
  const [activeScanAuthorized, setActiveScanAuthorized] = useState(false);
  const [scanJob, setScanJob] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [webResults, setWebResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedScanIndex, setSelectedScanIndex] = useState(0);

  const [urlVulTable, setUrlVulTable] = useState([]);
  const [urlEpssTable, setUrlEpssTable] = useState([]);

  const [TVulScore, setTVulScore] = useState(0);
  const [CVulScore, setCVulScore] = useState(0);
  const [saveScore, setSaveScore] = useState(0);
  const [auditResult, setAuditResult] = useState("");

  const [urlScanLoading, setUrlScanLoading] = useState(false);
  const [urlDashboardView, setUrlDashboardView] = useState(false);
  const [urlHeader, setUrlHeader] = useState(false);

  const fetchWebResults = useCallback(async (userId) => {
    try {
      const results = await getUrlResults(userId);
      setWebResults(results);
      if (results.length > 0) setUrlDashboardView(true);
      return results;
    } catch (error) {
      setWebResults([]);
      return [];
    }
  }, []);

  const loadWebData = useCallback(async (index, results = webResults) => {
    const scan = results[index];
    if (!scan) return;

    setCurrentIndex(index);
    setUrlVulTable(scan.vuln_data || []);
    setUrlEpssTable(scan.epss_data || []);
    setTVulScore(scan.t_vul);
    setCVulScore(scan.c_vul);
    setSaveScore(scan.ss);
    setAuditResult(scan.vuln_data || []);
  }, [webResults]);

  const waitForWebScan = useCallback(async (jobId) => {
    const startedPollingAt = Date.now();
    while (mountedRef.current && Date.now() - startedPollingAt < MAX_POLL_TIME_MS) {
      const response = await getUrlScanJob(userId, jobId);
      const nextJob = response?.data;
      if (!nextJob) throw new Error("The scan progress response was invalid");
      setScanJob(nextJob);

      if (TERMINAL_JOB_STATUSES.has(nextJob.status)) return nextJob;
      await wait(POLL_INTERVAL_MS);
    }

    if (!mountedRef.current) return null;
    throw new Error(
      "The scan is still running. You can leave this page and check the report again later."
    );
  }, [userId]);

  const finishWebScan = useCallback(async (jobId) => {
    const completedJob = await waitForWebScan(jobId);
    if (!completedJob || !mountedRef.current) return;
    if (completedJob.status === "failed") {
      throw new Error(
        completedJob.error?.message || completedJob.message || "The scan could not be completed"
      );
    }

    const results = await fetchWebResults(userId);
    const matchingIndex = results.findIndex(
      (result) => result.scan_uuid === completedJob.resultScanUuid
    );
    const latestIndex = matchingIndex >= 0 ? matchingIndex : results.length - 1;
    if (latestIndex < 0) throw new Error("The scan completed but its report could not be loaded");
    await loadWebData(latestIndex, results);
  }, [fetchWebResults, loadWebData, userId, waitForWebScan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setUrlScanLoading(true);
    setUrlDashboardView(true);
    setUrlHeader(false);

    try {
      let queued;
      try {
        queued = await scanUrl(userId, url, {
          authorizationConfirmed,
          activeScanAuthorized
        });
      } catch (submissionError) {
        if (
          submissionError.code === "SCAN_ALREADY_RUNNING" &&
          submissionError.data?.jobId
        ) {
          queued = { data: { jobId: submissionError.data.jobId } };
        } else {
          throw submissionError;
        }
      }
      const jobId = queued?.data?.jobId;
      if (!jobId) throw new Error("The scan was accepted without a job identifier");

      await finishWebScan(jobId);

    } catch (err) {
      setUrlDashboardView(false);
      setError({
        message: err.message,
        card: err.card || false
      });
    } finally {
      setUrlScanLoading(false);
      setUrlHeader(true);
      setUrl("");
      setAuthorizationConfirmed(false);
      setTimeout(() => {
        if (mountedRef.current) setError(null);
      }, 5000);
    }
  };

  const resetState = useCallback(() => {
    setWebLoading(false);
    setUrl("");
    setError(null);
    setAuthorizationConfirmed(false);
    setActiveScanAuthorized(false);
    setScanJob(null);

    setWebResults([]);
    setCurrentIndex(0);

    setUrlVulTable([]);
    setUrlEpssTable([]);

    setTVulScore(0);
    setCVulScore(0);
    setSaveScore(0);
    setAuditResult("");

    setUrlScanLoading(false);
    setUrlDashboardView(false);
    setUrlHeader(false);
  }, []);

  const initData = useCallback(async () => {
    if (!user) {
      resetState();
      return;
    }

    setWebLoading(true);
    try {
      const activeResponse = await getActiveUrlScanJob(userId);
      const activeJob = activeResponse?.data;
      if (activeJob?.jobId) {
        setScanJob(activeJob);
        setUrlScanLoading(true);
        setUrlDashboardView(true);
        setUrlHeader(false);
        setWebLoading(false);
        try {
          await finishWebScan(activeJob.jobId);
        } catch (activeScanError) {
          setUrlDashboardView(false);
          setError({ message: activeScanError.message, card: false });
        } finally {
          if (mountedRef.current) {
            setUrlScanLoading(false);
            setUrlHeader(true);
          }
        }
        return;
      }

      const results = await fetchWebResults(userId);
      if (!urlScanLoading && results.length > 0) {
        setUrlDashboardView(true);
        setUrlHeader(true);
        await loadWebData(0, results);
      }
    } finally {
      setWebLoading(false);
    }
  }, [
    fetchWebResults,
    finishWebScan,
    loadWebData,
    resetState,
    urlScanLoading,
    user,
    userId
  ]);

  return {
    // states
    webLoading,
    url,
    error,
    authorizationConfirmed,
    activeScanAuthorized,
    scanJob,

    webResults,
    currentIndex,
    selectedScanIndex,
    urlVulTable,
    urlEpssTable,

    TVulScore,
    CVulScore,
    saveScore,
    auditResult,

    urlScanLoading,
    urlDashboardView,
    urlHeader,

    // setters
    setUrl,
    setAuthorizationConfirmed,
    setActiveScanAuthorized,
    setCurrentIndex,
    setSelectedScanIndex,
    setUrlDashboardView,
    setUrlHeader,
    setUrlVulTable,

    // functions
    initData,
    loadWebData,
    handleSubmit,
    resetState,
  };
};

export default useWebData;
